/**
 * DuplicateBottleModal — shown when the user tries to add a wine that appears
 * to already be in the cellar.  Offers three choices:
 *   1. "Legg til én til"  – add another copy
 *   2. "Annen årgang"     – confirm / change vintage then add as new bottle
 *   3. "Hopp over"        – cancel
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal } from '@/design-system';
import type { Bottle, BottleCreate } from '@/lib/api';

export interface DuplicateMatch {
  existing: Bottle;
  /** The payload the user wanted to add. */
  payload: BottleCreate;
}

interface DuplicateBottleModalProps {
  match: DuplicateMatch | null;
  onAddAnother: (payload: BottleCreate) => void;
  onAddWithVintage: (payload: BottleCreate, vintage: string) => void;
  onSkip: () => void;
}

export function DuplicateBottleModal({
  match,
  onAddAnother,
  onAddWithVintage,
  onSkip,
}: DuplicateBottleModalProps) {
  const { t } = useTranslation();
  const [vintage, setVintage] = useState('');

  // Seed vintage field whenever a new match opens
  useEffect(() => {
    if (match) {
      setVintage(match.existing.vintage ?? match.payload.vintage ?? '');
    }
  }, [match]);

  const handleAddAnother = () => {
    if (match) onAddAnother(match.payload);
  };

  const handleAddWithVintage = () => {
    if (match) onAddWithVintage(match.payload, vintage.trim());
  };

  return (
    <Modal
      open={Boolean(match)}
      onClose={onSkip}
      title={t('duplicate.title')}
      size="sm"
    >
      <div className="grid gap-4">
        <p className="text-sm text-mist">
          {t('duplicate.body', { name: match?.existing.name ?? '' })}
        </p>

        <div className="grid gap-2">
          <Button
            variant="primary"
            iconLeft="add-bottle"
            onClick={handleAddAnother}
            className="w-full"
          >
            {t('duplicate.addAnother')}
          </Button>

          <div className="grid gap-2 rounded-md border border-line p-3">
            <p className="text-xs uppercase tracking-widest text-mist">
              {t('duplicate.otherVintage')}
            </p>
            <Input
              label={t('duplicate.vintageLabel')}
              inputMode="numeric"
              placeholder="f.eks. 2021"
              value={vintage}
              onChange={(e) => setVintage(e.target.value)}
            />
            <Button
              variant="ghost"
              iconLeft="edit"
              onClick={handleAddWithVintage}
              disabled={!vintage.trim()}
              className="w-full"
            >
              {t('duplicate.otherVintage')} — legg til
            </Button>
          </div>

          <Button
            variant="ghost"
            onClick={onSkip}
            className="w-full text-mist"
          >
            {t('duplicate.skip')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Normalised matching helpers ──────────────────────────────────────────────

function normalise(s: string | undefined): string {
  return (s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Returns the first bottle in `cellar` that is a likely duplicate of `payload`.
 * A duplicate is: same normalised name+producer (case/space-insensitive).
 * Same name+producer+vintage is a strong duplicate.
 */
export function findDuplicate(
  payload: BottleCreate,
  cellar: Bottle[],
): Bottle | null {
  const name = normalise(payload.name);
  const producer = normalise(payload.producer);
  if (!name) return null;

  const strong = cellar.find(
    (b) =>
      normalise(b.name) === name &&
      normalise(b.producer) === producer &&
      normalise(b.vintage) === normalise(payload.vintage) &&
      b.status === 'cellared',
  );
  if (strong) return strong;

  // Weaker: same name+producer, any vintage
  const weak = cellar.find(
    (b) =>
      normalise(b.name) === name &&
      normalise(b.producer) === producer &&
      b.status === 'cellared',
  );
  return weak ?? null;
}
